import type {
  BoosterPropertiesDto,
  BigBallPropertiesDto,
  BoostersPropertiesConfigDto,
  BoosterPriceOfferDto,
  BoostersPricesConfigDto,
  CumulativeBoosterInventoryDto,
  FireworksPropertiesDto,
  InfiniteBallsPropertiesDto,
  MultiballPropertiesDto,
  DynamitePropertiesDto,
  RocketPropertiesDto,
  MinePropertiesDto,
  BoosterActivationPhase,
} from "../api/dto/cumulative_boosters.dto.js";
import type { PlayerDto } from "../api/dto/player.dto.js";
import { ConfigHashStore } from "../config_sync/config_hash_store.js";
import {
  consumeBooster as consumeBoosterRequest,
  getBoostersPropertiesConfig,
  getBoostersPricesConfig,
  purchaseBooster as purchaseBoosterRequest,
} from "../api/requests/cumulative_boosters.requests.js";
import type { PurchaseBoosterResponse } from "../api/dto/cumulative_boosters.dto.js";
import { NetworkError } from "../api/http_client.js";
import { getPlayer } from "../api/requests/player.requests.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { CumulativeBoosterID, cumulativeBoosterOfferType } from "./cumulative_booster_id.js";
import { OfferTriggerController } from "../offers/offer_trigger_controller.js";
import {
  CumulativeBoosterCountListener,
  CumulativeBoosterInventory,
} from "./cumulative_booster_inventory.js";
import { NetworkManager } from "../network_manager.js";
import { isConfigMissing, isNetworkishError } from "../utils/network_errors.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { OfflineJournal } from "../offline/offline_journal.js";
import { shouldGoLocal } from "../offline/offline_policy.js";
import { LocalMetrics } from "../offline/local_metrics.js";
import { KeyHuntSignals } from "../key_hunt/key_hunt_signals.js";
import { PASSIVE_BOOSTER_STUB_CONFIGS, getPassiveBoosterStubConfig } from "./passive_booster_stub_config.js";

const PRICES_CACHE_KEY = "boosters_prices_v1";
const PROPERTIES_CACHE_KEY = "boosters_properties_v1";
const PASSIVE_STUB_INVENTORY_CACHE_KEY = "passive_boosters_v1";

const DAILY_TASK_BOOSTER_PARAMS: Record<CumulativeBoosterID, string> = {
  [CumulativeBoosterID.InfiniteBalls]: "infinite_balls",
  [CumulativeBoosterID.BigBall]: "big_balls",
  [CumulativeBoosterID.Fireworks]: "fireworks",
  [CumulativeBoosterID.Multiball]: "multiballs",
  [CumulativeBoosterID.Dynamite]: "dynamite",
  [CumulativeBoosterID.Rocket]: "rockets",
  [CumulativeBoosterID.Mine]: "mines",
};

class CumulativeBoosterServiceImpl
{
  public readonly inventory = new CumulativeBoosterInventory();
  private prices_config: BoostersPricesConfigDto | null = PersistentCache.read<BoostersPricesConfigDto>( PRICES_CACHE_KEY );
  private properties_config: BoostersPropertiesConfigDto | null = PersistentCache.read<BoostersPropertiesConfigDto>( PROPERTIES_CACHE_KEY );
  private mutation_chain: Promise<void> = Promise.resolve();
  private readonly pending_mutation_counts = new Map<CumulativeBoosterID, number>();
  private pending_mutation_total = 0;

  public constructor()
  {
    this.resetPassiveBoosterStubInventory( true );
    this.inventory.subscribe( id =>
    {
      if ( id === CumulativeBoosterID.Dynamite )
        this.persistPassiveBoosterStubInventory();
    } );
  }

  public initializeFromPlayer( player: PlayerDto ): void
  {
    this.applyInventory( player );
  }

  public reset(): void
  {
    this.inventory.setCount( CumulativeBoosterID.InfiniteBalls, 0 );
    this.inventory.setCount( CumulativeBoosterID.BigBall, 0 );
    this.inventory.setCount( CumulativeBoosterID.Fireworks, 0 );
    this.inventory.setCount( CumulativeBoosterID.Multiball, 0 );
    this.inventory.setCount( CumulativeBoosterID.Rocket, 0 );
    this.inventory.setCount( CumulativeBoosterID.Mine, 0 );
    PersistentCache.remove( PASSIVE_STUB_INVENTORY_CACHE_KEY );
    this.resetPassiveBoosterStubInventory();
    this.clearPricesConfig();
    this.clearPropertiesConfig();
    ConfigHashStore.remove( "boosters_prices" );
    ConfigHashStore.remove( "boosters_properties" );
  }

  public applyInventory( inventory: CumulativeBoosterInventoryDto ): void
  {
    this.inventory.setCount( CumulativeBoosterID.InfiniteBalls, inventory.infinite_balls );
    this.inventory.setCount( CumulativeBoosterID.BigBall, inventory.big_balls );
    this.inventory.setCount( CumulativeBoosterID.Fireworks, inventory.fireworks );
    this.inventory.setCount( CumulativeBoosterID.Multiball, inventory.multi_balls );
    // Dynamite remains client-owned; Rocket and Mine are server-owned. Partial
    // inventories (older servers, event responses) must not zero absent fields.
    if ( inventory.dynamites !== undefined )
      this.inventory.setCount( CumulativeBoosterID.Dynamite, inventory.dynamites );
    if ( inventory.rockets !== undefined )
      this.inventory.setCount( CumulativeBoosterID.Rocket, inventory.rockets );
    if ( inventory.mines !== undefined )
      this.inventory.setCount( CumulativeBoosterID.Mine, inventory.mines );
    this.mirrorToStats();
  }

  private mirrorToStats(): void
  {
    const stats = StatisticsStorage.load();
    const next = {
      fireworks: this.inventory.getCount( CumulativeBoosterID.Fireworks ),
      multiballs: this.inventory.getCount( CumulativeBoosterID.Multiball ),
      big_balls: this.inventory.getCount( CumulativeBoosterID.BigBall ),
      infinite_balls: this.inventory.getCount( CumulativeBoosterID.InfiniteBalls ),
    };
    if ( stats.fireworks === next.fireworks && stats.multiballs === next.multiballs
      && stats.big_balls === next.big_balls && stats.infinite_balls === next.infinite_balls )
      return;
    StatisticsStorage.update( next );
  }

  public getInventory(): CumulativeBoosterInventoryDto
  {
    return {
      infinite_balls: this.inventory.getCount( CumulativeBoosterID.InfiniteBalls ),
      big_balls:      this.inventory.getCount( CumulativeBoosterID.BigBall ),
      fireworks:      this.inventory.getCount( CumulativeBoosterID.Fireworks ),
      multi_balls:    this.inventory.getCount( CumulativeBoosterID.Multiball ),
      dynamites:      this.inventory.getCount( CumulativeBoosterID.Dynamite ),
      rockets:        this.inventory.getCount( CumulativeBoosterID.Rocket ),
      mines:          this.inventory.getCount( CumulativeBoosterID.Mine ),
    };
  }

  public subscribe( listener: CumulativeBoosterCountListener ): () => void
  {
    return this.inventory.subscribe( listener );
  }

  public setPricesConfig( config: BoostersPricesConfigDto ): void
  {
    this.prices_config = config;
  }

  public hasCachedConfig( type: string ): boolean
  {
    if ( type === "boosters_prices" ) return PersistentCache.read<unknown>( PRICES_CACHE_KEY ) !== null;
    if ( type === "boosters_properties" ) return PersistentCache.read<unknown>( PROPERTIES_CACHE_KEY ) !== null;
    return true;
  }

  public clearPricesConfig(): void
  {
    this.prices_config = null;
  }

  public clearPropertiesConfig(): void
  {
    this.properties_config = null;
  }

  public async refreshPropertiesConfig(): Promise<boolean>
  {
    if ( !NetworkManager.isOnline() )
      return this.properties_config !== null;

    try
    {
      const config = await getBoostersPropertiesConfig();
      console.log( '[Boosters] Loaded properties config', config );
      console.table( config.boosters.map( booster => ( {
        ...booster,
        config_source: 'server',
      } ) ) );
      this.properties_config = config;
      PersistentCache.write( PROPERTIES_CACHE_KEY, config );
      return true;
    }
    catch ( e )
    {
      if ( isConfigMissing( e ) )
      {
        this.properties_config = null;
        PersistentCache.remove( PROPERTIES_CACHE_KEY );
        return false;
      }
      console.warn( '[Boosters] Failed to load properties config, keeping cached', e );
      return this.properties_config !== null;
    }
  }

  public async refreshPricesConfig(): Promise<boolean>
  {
    if ( !NetworkManager.isOnline() )
      return this.prices_config !== null;

    try
    {
      const config = await getBoostersPricesConfig();
      console.log( '[Boosters] Loaded prices config', config );
      console.table( config.boosters_prices.flatMap( booster =>
        booster.prices.map( offer => ( {
          booster_id: booster.booster_id,
          offer_id: offer.id,
          price: offer.price,
          amount: offer.amount,
          config_source: 'server',
        } ) )
      ) );
      this.setPricesConfig( config );
      PersistentCache.write( PRICES_CACHE_KEY, config );
      return true;
    }
    catch ( e )
    {
      if ( isConfigMissing( e ) )
      {
        this.prices_config = null;
        PersistentCache.remove( PRICES_CACHE_KEY );
        return false;
      }
      console.warn( '[Boosters] Failed to load prices config, keeping cached', e );
      return this.prices_config !== null;
    }
  }

  public consume( booster_id: CumulativeBoosterID ): Promise<boolean>
  {
    return this.enqueueMutation(
      booster_id,
      async () => this.consumeImmediately( booster_id )
    );
  }

  public hasPendingMutation(): boolean
  {
    return this.pending_mutation_total > 0;
  }

  private async consumeImmediately( booster_id: CumulativeBoosterID ): Promise<boolean>
  {
    if ( shouldGoLocal() )
      return this.consumeLocally( booster_id );

    try
    {
      const response = await consumeBoosterRequest( booster_id );
      this.applyInventory( response.boosters );
      KeyHuntSignals.serverProgressMayHaveChanged();
      void OfferTriggerController.report( 'booster_used', {
        context: { booster_id, booster_type: cumulativeBoosterOfferType( booster_id ) },
      } );
      return true;
    }
    catch ( e )
    {
      if ( isNetworkishError( e ) )
        return this.consumeLocally( booster_id );
      console.warn( `[Boosters] Failed to consume booster ${booster_id}`, e );
      return false;
    }
  }

  private consumeLocally( booster_id: CumulativeBoosterID ): boolean
  {
    OfflineJournal.append( 'booster_consume', { booster_id } );
    LocalMetrics.report( [ { metric: 'use_boosters', amount: 1, param: DAILY_TASK_BOOSTER_PARAMS[booster_id] } ] );
    this.mirrorToStats();
    return true;
  }

  public getPricesConfig(): BoostersPricesConfigDto | null
  {
    return this.prices_config;
  }

  public getPropertiesConfig(): BoostersPropertiesConfigDto | null
  {
    return this.properties_config;
  }

  public getBoosterProperties( id: CumulativeBoosterID.InfiniteBalls ): InfiniteBallsPropertiesDto | null;
  public getBoosterProperties( id: CumulativeBoosterID.BigBall ): BigBallPropertiesDto | null;
  public getBoosterProperties( id: CumulativeBoosterID.Fireworks ): FireworksPropertiesDto | null;
  public getBoosterProperties( id: CumulativeBoosterID.Multiball ): MultiballPropertiesDto | null;
  public getBoosterProperties( id: CumulativeBoosterID.Dynamite ): DynamitePropertiesDto | null;
  public getBoosterProperties( id: CumulativeBoosterID.Rocket ): RocketPropertiesDto | null;
  public getBoosterProperties( id: CumulativeBoosterID.Mine ): MinePropertiesDto | null;
  public getBoosterProperties( id: CumulativeBoosterID ): BoosterPropertiesDto | null;
  public getBoosterProperties( id: CumulativeBoosterID ): BoosterPropertiesDto | null
  {
    return this.properties_config?.boosters.find( booster => booster.id === id ) ?? null;
  }

  /** The inventory was already decremented locally when activation was reserved. */
  public commitPassiveBoosterLocally( id: CumulativeBoosterID ): boolean
  {
    if ( this.getActivationPhase( id ) !== "level_start" )
      return false;

    console.info(
      `[PassiveBoosters] booster ${cumulativeBoosterOfferType( id )} completed; `
      + `remaining=${this.inventory.getCount( id )}`
    );
    return true;
  }

  public getActivationPhase( id: CumulativeBoosterID ): BoosterActivationPhase
  {
    return this.getBoosterProperties( id )?.activation_phase
      ?? ( id === CumulativeBoosterID.Dynamite || id === CumulativeBoosterID.Rocket || id === CumulativeBoosterID.Mine
        ? "level_start"
        : "in_game" );
  }

  public getUnlockLevel( id: CumulativeBoosterID ): number
  {
    const unlock_level = this.getBoosterProperties( id )?.unlock_level;
    return typeof unlock_level === "number"
      && Number.isInteger( unlock_level )
      && unlock_level > 0
      ? unlock_level
      : 1;
  }

  public isUnlocked( id: CumulativeBoosterID, player_level: number ): boolean
  {
    return Number.isFinite( player_level )
      && player_level >= this.getUnlockLevel( id );
  }

  public getPriceOffers( booster_id: CumulativeBoosterID ): readonly BoosterPriceOfferDto[]
  {
    const server_prices = this.prices_config?.boosters_prices.find(
      entry => entry.booster_id === booster_id
    )?.prices;
    if ( server_prices !== undefined )
      return server_prices;
    const stub_offer = getPassiveBoosterStubConfig( booster_id )?.price_offer;
    return stub_offer ? [ stub_offer ] : [];
  }

  public getFirstPriceOffer( booster_id: CumulativeBoosterID ): BoosterPriceOfferDto | null
  {
    return this.getPriceOffers( booster_id )[0] ?? null;
  }

  public async getOrLoadFirstPriceOffer(
    booster_id: CumulativeBoosterID
  ): Promise<BoosterPriceOfferDto | null>
  {
    const cached_offer = this.getFirstPriceOffer( booster_id );
    if ( cached_offer !== null && cached_offer.id >= 0 )
      return cached_offer;

    await this.refreshPricesConfig();
    return this.getFirstPriceOffer( booster_id );
  }

  public purchase(
    booster_id: CumulativeBoosterID,
    offer_id: number
  ): Promise<PurchaseBoosterResponse | null>
  {
    return this.enqueueMutation(
      booster_id,
      () => this.purchaseImmediately( booster_id, offer_id )
    );
  }

  private async purchaseImmediately(
    booster_id: CumulativeBoosterID,
    offer_id: number
  ): Promise<PurchaseBoosterResponse | null>
  {
    const inventory_before = this.getInventory();
    const coins_before = StatisticsStorage.load().coins;
    const offer = this.getPriceOffers( booster_id ).find(
      candidate => candidate.id === offer_id
    ) ?? null;

    // Client-only boosters: the server knows neither the item nor its price.
    if ( getPassiveBoosterStubConfig( booster_id ) !== null )
      return this.purchaseLocally( booster_id, offer_id, offer, false );

    if ( shouldGoLocal() )
      return this.purchaseLocally( booster_id, offer_id, offer );

    try
    {
      const response = await purchaseBoosterRequest( booster_id, offer_id );
      StatisticsStorage.update( { coins: response.coins } );
      this.applyInventory( response.boosters );
      return response;
    }
    catch ( e )
    {
      // A timeout/abort does not mean the server rejected the purchase: it may
      // have committed it and only the response was lost. Reconcile before the
      // UI allows another tap, otherwise one pack can be bought twice.
      if ( e instanceof NetworkError && offer !== null )
      {
        if ( !NetworkManager.isOnline() )
          return this.purchaseLocally( booster_id, offer_id, offer );

        const reconciled = await this.reconcileAmbiguousPurchase(
          booster_id,
          offer_id,
          offer.price,
          offer.amount,
          inventory_before,
          coins_before
        );
        if ( reconciled !== null )
          return reconciled;
      }

      console.warn(
        `[Boosters] Failed to purchase booster ${booster_id}, offer ${offer_id}`,
        e
      );
      return null;
    }
  }

  private purchaseLocally(
    booster_id: CumulativeBoosterID,
    offer_id: number,
    offer: BoosterPriceOfferDto | null,
    sync_with_server = true
  ): PurchaseBoosterResponse | null
  {
    if ( offer === null )
    {
      console.warn( `[Boosters] offline purchase of ${booster_id} needs a cached price offer` );
      return null;
    }

    const stats = StatisticsStorage.load();
    if ( stats.coins < offer.price )
      return null;

    StatisticsStorage.update( { coins: stats.coins - offer.price } );
    this.inventory.add( booster_id, offer.amount );
    this.mirrorToStats();
    if ( sync_with_server )
      OfflineJournal.append( 'booster_purchase', { booster_id, offer_id } );
    if ( offer.price > 0 )
      LocalMetrics.report( [ { metric: 'spend_coins', amount: offer.price } ] );

    return {
      coins: stats.coins - offer.price,
      boosters: this.getInventory(),
      purchased: { booster_id, offer_id, price: offer.price, amount: offer.amount },
    };
  }

  private async reconcileAmbiguousPurchase(
    booster_id: CumulativeBoosterID,
    offer_id: number,
    expected_price: number,
    expected_amount: number,
    inventory_before: CumulativeBoosterInventoryDto,
    coins_before: number
  ): Promise<PurchaseBoosterResponse | null>
  {
    try
    {
      const player = await getPlayer();
      const server_inventory = this.inventoryDtoForLog( player );
      const count_before = this.countFromDto( inventory_before, booster_id );
      const count_after = this.countFromDto( server_inventory, booster_id );
      const inventory_increased = count_after >= count_before + expected_amount;
      const coins_decreased = player.coins <= coins_before - expected_price;

      StatisticsStorage.update( { coins: player.coins } );
      this.applyInventory( player );

      if ( !inventory_increased || !coins_decreased )
        return null;

      return {
        coins: player.coins,
        boosters: server_inventory,
        purchased: {
          booster_id,
          offer_id,
          price: expected_price,
          amount: expected_amount,
        },
      };
    }
    catch
    {
      return null;
    }
  }

  private enqueueMutation<T>(
    booster_id: CumulativeBoosterID,
    operation: () => Promise<T>
  ): Promise<T>
  {
    const previous = this.mutation_chain;
    const pending_count = ( this.pending_mutation_counts.get( booster_id ) ?? 0 ) + 1;
    this.pending_mutation_counts.set( booster_id, pending_count );
    this.pending_mutation_total++;
    const result = previous
      .catch( () => {} )
      .then( () => operation() );

    const barrier = result.then( () => {}, () => {} );
    this.mutation_chain = barrier;
    void barrier.finally( () =>
    {
      const remaining = Math.max(
        0,
        ( this.pending_mutation_counts.get( booster_id ) ?? 1 ) - 1
      );
      if ( remaining === 0 )
        this.pending_mutation_counts.delete( booster_id );
      else
        this.pending_mutation_counts.set( booster_id, remaining );

      this.pending_mutation_total = Math.max( 0, this.pending_mutation_total - 1 );

    } );

    return result;
  }

  private inventoryDtoForLog(
    inventory: CumulativeBoosterInventoryDto
  ): CumulativeBoosterInventoryDto
  {
    return {
      infinite_balls: inventory.infinite_balls,
      big_balls: inventory.big_balls,
      fireworks: inventory.fireworks,
      multi_balls: inventory.multi_balls,
      dynamites: inventory.dynamites ?? 0,
      rockets: inventory.rockets ?? 0,
      mines: inventory.mines ?? 0,
    };
  }

  private countFromDto(
    inventory: CumulativeBoosterInventoryDto,
    booster_id: CumulativeBoosterID
  ): number
  {
    switch ( booster_id )
    {
      case CumulativeBoosterID.InfiniteBalls: return inventory.infinite_balls;
      case CumulativeBoosterID.BigBall:       return inventory.big_balls;
      case CumulativeBoosterID.Fireworks:     return inventory.fireworks;
      case CumulativeBoosterID.Multiball:     return inventory.multi_balls;
      case CumulativeBoosterID.Dynamite:      return inventory.dynamites ?? 0;
      case CumulativeBoosterID.Rocket:        return inventory.rockets ?? 0;
      case CumulativeBoosterID.Mine:          return inventory.mines ?? 0;
    }
  }

  private resetPassiveBoosterStubInventory( restore_saved = false ): void
  {
    const saved = restore_saved
      ? PersistentCache.read<Record<string, number>>( PASSIVE_STUB_INVENTORY_CACHE_KEY )
      : null;
    for ( const config of PASSIVE_BOOSTER_STUB_CONFIGS )
    {
      const count = saved?.[config.id];
      this.inventory.setCount( config.id,
        typeof count === "number" && Number.isFinite( count ) && count >= 0
          ? count
          : config.initial_count
      );
    }
    if ( saved !== null && saved !== undefined && String( CumulativeBoosterID.Mine ) in saved )
      this.persistPassiveBoosterStubInventory();
  }

  private persistPassiveBoosterStubInventory(): void
  {
    PersistentCache.write( PASSIVE_STUB_INVENTORY_CACHE_KEY, {
      [CumulativeBoosterID.Dynamite]: this.inventory.getCount( CumulativeBoosterID.Dynamite ),
    } );
  }
}

export const CumulativeBoosterService = new CumulativeBoosterServiceImpl();
